/* global L */
import PropTypes from 'prop-types';
import { Component } from 'react';

import { CLUSTERED_MARKERS_PER_ITERATION } from 'hb-react/shared/constants';
import { withFeatureFlag } from 'hb-react/shared/hooks/useFeatureFlag';

import markerImg from './LeafletMap/marker.png';

require('leaflet/dist/leaflet-src');
require('leaflet-providers/leaflet-providers');
require('leaflet.markercluster/dist/leaflet.markercluster');

class Map extends Component {
  componentDidMount() {
    const {
      center: { lat, lng },
      keyboard,
      maxZoom,
      markersUrl,
      zoom,
      enable_zoom: enableZoom,
      mapOptions,
      onMounted,
    } = this.props;

    const bounds = L.latLngBounds(L.latLng(85, -180), L.latLng(-85, 180));

    this.map = L.map(this.map_container.id, {
      center: [lat, lng],
      keyboard,
      maxZoom,
      zoom: parseInt(zoom, 10),
      zoomControl: false,
      maxBounds: bounds,
    });

    new L.Control.Zoom({ position: 'topright' }).addTo(this.map);
    if (!enableZoom) {
      this.map.scrollWheelZoom.disable();
    }

    const layer = `https://api.mapbox.com/styles/v1/kit-united/${this.getLayer()}/tiles/256/{z}/{x}/{y}?access_token=${
      window.__HB_ENV__.MAPBOX_KEY
    }`;

    L.tileLayer(layer, mapOptions).addTo(this.map);

    this.addMarker();
    if (markersUrl) this.fetchMarkers();
    // Refresh the map to fully fill the container (dom updated while the map init)
    this.map._onResize();

    // To remove the Ukrainian flag and "Leaflet" label
    this.map.attributionControl?.setPrefix(false);

    this.disableKeyboardNavigation();

    if (onMounted) {
      onMounted(this);
    }
  }

  getLayer = () => {
    const { light_style: lightStyle } = this.props;
    let layerId;

    if (lightStyle) {
      layerId = window.__HB_ENV__.MAPBOX_LIGHT_LAYER_EN;

      if (I18n.locale === 'fr') {
        layerId = window.__HB_ENV__.MAPBOX_LIGHT_LAYER_FR;
      }
    } else {
      layerId = window.__HB_ENV__.MAPBOX_DEFAULT_LAYER_EN;

      if (I18n.locale === 'fr') {
        layerId = window.__HB_ENV__.MAPBOX_DEFAULT_LAYER_FR;
      }
    }

    return layerId;
  };

  addMarker = () => {
    const {
      marker: { lat, lng },
      zoom,
      onMarkerClickHandler,
    } = this.props;

    if (lat && lng) {
      const mapIcon = L.icon({
        iconUrl: markerImg,
        iconSize: [70, 70],
      });

      this.map.setView(new L.LatLng(lat, lng), zoom);

      const options = {
        icon: mapIcon,
      };

      options.keyboard = !!onMarkerClickHandler;

      const marker = L.marker([lat, lng], options).addTo(this.map);

      if (onMarkerClickHandler) marker.on('click', onMarkerClickHandler);
    }
  };

  disableKeyboardNavigation = () => {
    if (!this.props.keyboard) {
      // This is for removing keyboard navigation for all the little markers
      document
        .querySelectorAll(
          '#map__container [tabindex="0"], .leaflet-control-zoom a, .leaflet-control-attribution a',
        )
        .forEach((el) => {
          el.setAttribute('tabindex', '-1');
          el.setAttribute('href', 'google.com');
        });
    }
  };

  fetchMarkers = () => {
    const clusterGroup = L.markerClusterGroup({
      spiderfyOnMaxZoom: false,
      chunkedLoading: true,
      animateAddingMarkers: false,
    });

    clusterGroup.on('clusterclick', (e) => {
      const cluster = e.layer;
      const childCount = cluster._childCount;
      const MAX_CHILD_COUNT_FOR_DECLUSTERING = 5;

      if (childCount < MAX_CHILD_COUNT_FOR_DECLUSTERING) {
        cluster.spiderfy();

        return false;
      }

      return true;
    });

    // eslint-disable-next-line jquery/no-ajax, no-undef
    $.get(this.props.markersUrl).done((data) => {
      if (data && data.markers) {
        let markers = [];

        for (let i = 0, l = data.markers.length - 1; i < l; i++) {
          if (
            i % CLUSTERED_MARKERS_PER_ITERATION === 0 &&
            markers.length !== 0
          ) {
            clusterGroup.addLayer(
              L.geoJson(markers, {
                onEachFeature: (feature, layer) =>
                  layer.setIcon(L.icon(feature.properties.icon)),
              }),
            );
            markers = [];
          }
          markers.push(data.markers[i]);
        }

        if (markers.length !== 0) {
          clusterGroup.addLayer(
            L.geoJson(markers, {
              onEachFeature: (feature, layer) =>
                layer.setIcon(L.icon(feature.properties.icon)),
            }),
          );
        }
        this.map.addLayer(clusterGroup);
        this.disableKeyboardNavigation();
      }
    });
  };

  render() {
    const { height, keyboard } = this.props;

    return (
      <div
        aria-hidden={!keyboard}
        data-testid="map__container"
        id="map__container"
        style={{ height: `${parseInt(height, 10)}px`, width: '100%' }}
        ref={(ref) => (this.map_container = ref)}
      />
    );
  }
}

Map.propTypes = {
  center: PropTypes.object,
  enable_zoom: PropTypes.bool,
  height: PropTypes.number,
  keyboard: PropTypes.bool,
  light_style: PropTypes.bool,
  maxZoom: PropTypes.number,
  marker: PropTypes.object,
  mapOptions: PropTypes.object,
  onMarkerClickHandler: PropTypes.func,
  zoom: PropTypes.number,
};

Map.defaultProps = {
  center: { lat: 48, lng: 2 }, // Paris France, center of the map
  enable_zoom: false,
  // eslint-disable-next-line no-undef
  height: $(window).height() / 2.7,
  keyboard: false,
  light_style: false,
  maxZoom: 14,
  marker: { lat: null, lng: null },
  mapOptions: {
    id: window.__HB_ENV__.MAPBOX_ID,
    accessToken: window.__HB_ENV__.MAPBOX_KEY,
    continuousWorld: false,
    noWrap: true,
    detectRetina: true,
  },
  zoom: 3,
};

export default withFeatureFlag(Map);
